<template>
  <main-large-dialog
    :is-visible.sync="computedIsVisible"
    :title="$i18n.t('dialog.title.modelingOptions')"
    agree-text="dialog.save"
    dialog-width="60%"
    min-body-height="70vh"
    max-height="80vh"
    no-paddings-y
    no-paddings-x
    @agree="handleSettingsChange"
    :loading="isChanging"
  >
    <template #body>
      <main-loader v-if="isFetching"></main-loader>
      <v-tabs v-model="currentTab" class="fill-height d-flex flex-column" hide-slider color="font" v-else>
        <v-tab
          v-for="tab in tabs"
          :key="tab.value"
          v-text="tab.text"
          active-class="activeTab font-weight-medium"
          class="font-weight-regular font--text"
          style="text-transform: none;"
        ></v-tab>
        <v-tabs-items v-model="currentTab">
          <v-row no-gutters style="background-color: white">
            <v-col cols="auto" class="pl-5 py-8">
              <v-icon color="primary" class="mr-2" size="18">mdi-information</v-icon>
              <i18n path="dialog.body.modelingOptions.info" tag="span">
                <template #link>
                  <a target="_blank" :href="url" style="line-height: 20px">
                    {{ $i18n.t('dialog.body.modelingOptions.link') }}</a
                  >
                </template>
              </i18n>
            </v-col>
          </v-row>
          <v-tab-item style="height: inherit" :transition="false" v-for="(row, index) in tabSettings" :key="index">
            <modeling-options-tab :row="row" @input="setValue($event)"></modeling-options-tab>
          </v-tab-item>
        </v-tabs-items>
      </v-tabs>
    </template>
  </main-large-dialog>
</template>

<script>
import { call, get } from 'vuex-pathify';
import { startCase, camelCase, cloneDeep } from 'lodash';
import ModelingOptionsTab from '@/components/ModelingOptionsTab';
import MainLoader from '@/components/MainLoader';
import MainLargeDialog from '@/components/MainLargeDialog';
export default {
  name: 'ModelingOptionsDialog',
  components: { MainLoader, ModelingOptionsTab, MainLargeDialog },
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
  },
  data: () => ({
    isFetching: false,
    isChanging: false,
    currentTab: 0,
    url: 'https://epanet22.readthedocs.io/en/latest/input_keywords.html?#options',
    unitsValues: ['CFS', 'GPM', 'MGD', 'IMGD', 'AFD', 'LPS', 'LPM', 'MLD', 'CMH', 'CMD'],
    headlossValues: ['H-W', 'D-W', 'C-M'],
    unbalancedValues: ['CONTINUE', 'STOP'],
    qualityValues: ['NONE', 'CHEMICAL', 'AGE', 'TRACE'],
    massUnitsValues: ['mg/L', 'ug/L'],
    orderWallValues: ['0', '1'],
    statisticValues: ['NONE', 'AVERAGE', 'MINIMUM', 'MAXIMUM', 'RANGE'],
    body: {},
    dataTypesSelect: ['UNITS', 'HEADLOSS', 'UNBALANCED', 'MASS UNITS', 'ORDER WALL', 'STATISTIC'],
    dataTypesCombobox: ['PATTERN', 'QUALITY'],
    dataTypesInteger: ['TRIALS', 'UNBALANCED_TRIES', 'CHECKFREQ', 'MAXCHECK', 'DAMPLIMIT'],
    dataTypesFloat: [
      'ACCURACY',
      'DEMAND MULTIPLIER',
      'EMITTER EXPONENT',
      'TOLERANCE',
      'ORDER BULK',
      'GLOBAL WALL',
      'GLOBAL BULK',
      'LIMITING POTENTIAL',
      'ROUGHNESS CORRELATION',
      'GLOBAL EFFIC',
      'GLOBAL PRICE',
      'DEMAND CHARGE',
      'DIFFUSIVITY',
      'SPECIFIC GRAVITY',
      'VISCOSITY',
    ],
    dataTypesTime: [
      'HYDRAULIC TIMESTEP',
      'QUALITY TIMESTEP',
      'PATTERN TIMESTEP',
      'PATTERN START',
      'REPORT TIMESTEP',
      'REPORT START',
      'START CLOCKTIME',
    ],
    dataTypesText: ['TRACE NODE', 'DURATION'],
    hydraulicTab: [
      'UNITS',
      'HEADLOSS',
      'SPECIFIC GRAVITY',
      'VISCOSITY',
      'TRIALS',
      'ACCURACY',
      'UNBALANCED',
      'UNBALANCED_TRIES',
      'PATTERN',
      'DEMAND MULTIPLIER',
      'EMITTER EXPONENT',
      'CHECKFREQ',
      'MAXCHECK',
      'DAMPLIMIT',
    ],
    qualityTab: ['QUALITY', 'TRACE NODE', 'MASS UNITS', 'DIFFUSIVITY', 'TOLERANCE'],
    reactionTab: [
      'ORDER BULK',
      'ORDER WALL',
      'GLOBAL WALL',
      'GLOBAL BULK',
      'LIMITING POTENTIAL',
      'ROUGHNESS CORRELATION',
    ],
    timeTab: [
      'DURATION',
      'HYDRAULIC TIMESTEP',
      'QUALITY TIMESTEP',
      'PATTERN TIMESTEP',
      'PATTERN START',
      'REPORT TIMESTEP',
      'REPORT START',
      'START CLOCKTIME',
      'STATISTIC',
    ],
    energyTab: ['GLOBAL EFFIC', 'GLOBAL PRICE', 'DEMAND CHARGE'],
  }),
  computed: {
    settingsValues: get('admin/settingsValues.epanet_options'),
    patterns: get('epanet/patterns'),
    patternNames() {
      return this.patterns.map(pattern => pattern.name);
    },
    patternValues() {
      return ['NONE', ...this.patternNames];
    },
    computedSettings() {
      return Object.keys(this.body).map(key => {
        return {
          name: key,
          setting: key,
          hydraulicIndex: this.getItemIndex(key, this.hydraulicTab),
          qualityIndex: this.getItemIndex(key, this.qualityTab),
          reactionIndex: this.getItemIndex(key, this.reactionTab),
          timeIndex: this.getItemIndex(key, this.timeTab),
          energyIndex: this.getItemIndex(key, this.energyTab),
          value: this.body[key],
          translation: this.$i18n.t(`dialog.body.modelingOptions.settings.${key}`),
          subtitle: this.getSubtitle(key),
          dataType: this.getDataType(key),
          selectItems: this.getSelectItems(key),
          explanation: this.getExplanation(key),
        };
      });
    },
    tabSettings() {
      return [
        this.computedSettings
          .filter(item => this.hydraulicTab.includes(item.name))
          .sort((a, b) => a.hydraulicIndex - b.hydraulicIndex),
        this.computedSettings
          .filter(item => this.qualityTab.includes(item.name))
          .sort((a, b) => a.qualityIndex - b.qualityIndex),
        this.computedSettings
          .filter(item => this.reactionTab.includes(item.name))
          .sort((a, b) => a.reactionIndex - b.reactionIndex),
        this.computedSettings
          .filter(item => this.timeTab.includes(item.name))
          .sort((a, b) => a.timeIndex - b.timeIndex),
        this.computedSettings
          .filter(item => this.energyTab.includes(item.name))
          .sort((a, b) => a.energyIndex - b.energyIndex),
      ];
    },
    tabs() {
      return [
        { text: this.$i18n.t('dialog.body.modelingOptions.tabs.hydraulic'), value: 'hydraulic' },
        { text: this.$i18n.t('dialog.body.modelingOptions.tabs.waterQuality'), value: 'waterQuality' },
        { text: this.$i18n.t('dialog.body.modelingOptions.tabs.reaction'), value: 'reaction' },
        { text: this.$i18n.t('dialog.body.modelingOptions.tabs.time'), value: 'time' },
        { text: this.$i18n.t('dialog.body.modelingOptions.tabs.energy'), value: 'energy' },
      ];
    },
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
  },
  methods: {
    getPatterns: call('epanet/getPatterns'),
    editSingleSetting: call('admin/editSingleSetting'),
    getSingleSetting: call('admin/getSingleSetting'),
    getItemIndex(key, array) {
      return array.indexOf(key);
    },

    getExplanation(key) {
      if (this.$i18n.te(`dialog.body.modelingOptions.explanation.${key}`) !== false) {
        return this.$i18n.t(`dialog.body.modelingOptions.explanation.${key}`);
      }
    },
    setValue(value) {
      const key = Object.keys(this.body).find(k => k === value.settingName);
      this.body[key] = value.newValue;
    },
    async handleSettingsChange() {
      this.isChanging = true;
      const value = {};
      value.value = this.body;
      await this.editSingleSetting({
        key: 'epanet_options',
        body: value,
      });
      this.isChanging = false;
      this.computedIsVisible = false;
    },
    getSelectTranslation(key, setting) {
      return this.$i18n.t(`dialog.body.modelingOptions.selectValues.${key}.${setting}`);
    },
    getSelectItems(key) {
      switch (key) {
        case 'UNITS':
          return this.unitsValues.map(value => {
            return {
              name: this.getSelectTranslation(key, value),
              value: value,
            };
          });
        case 'HEADLOSS':
          return this.headlossValues.map(value => {
            return {
              name: this.getSelectTranslation(key, value),
              value: value,
            };
          });
        case 'UNBALANCED':
          return this.unbalancedValues.map(value => {
            return {
              name: this.getSelectTranslation(key, value),
              value: value,
            };
          });
        case 'PATTERN':
          return this.patternValues.map(value => {
            if (value === 'NONE') {
              return {
                name: this.getSelectTranslation(key, value),
                value: value,
              };
            } else {
              return {
                name: value,
                value: value,
              };
            }
          });
        case 'QUALITY':
          return this.qualityValues.map(value => {
            return {
              name: this.getSelectTranslation(key, value),
              value: value,
            };
          });
        case 'MASS UNITS':
          return this.massUnitsValues.map(value => {
            return {
              name: this.getSelectTranslation(key, value),
              value: value,
            };
          });
        case 'ORDER WALL':
          return this.orderWallValues.map(value => {
            return {
              name: this.getSelectTranslation(key, value),
              value: Number.parseInt(value),
            };
          });
        case 'STATISTIC':
          return this.statisticValues.map(value => {
            return {
              name: this.getSelectTranslation(key, value),
              value: value,
            };
          });
      }
    },
    getSubtitle(value) {
      if (value === 'CHECKFREQ' || value === 'MAXCHECK' || value === 'DAMPLIMIT') {
        return '';
      } else {
        return startCase(camelCase(value));
      }
    },

    getDataType(key) {
      if (this.dataTypesSelect.includes(key)) {
        return { name: 'select' };
      }
      if (this.dataTypesCombobox.includes(key)) {
        return { name: 'combobox' };
      }
      if (this.dataTypesInteger.includes(key)) {
        return { name: 'integer' };
      }
      if (this.dataTypesFloat.includes(key)) {
        return { name: 'float' };
      }
      if (this.dataTypesTime.includes(key)) {
        return { name: 'time' };
      }
      if (this.dataTypesText.includes(key)) {
        return { name: 'text' };
      }
    },
    async init() {
      this.isFetching = true;
      await Promise.all([this.$nextTick(), this.getPatterns(), this.getSingleSetting('epanet_options')]);
      this.body = cloneDeep(this.settingsValues);
      this.isFetching = false;
    },
  },
  watch: {
    computedIsVisible: {
      immediate: true,
      handler(nV) {
        if (nV) {
          this.init();
        }
      },
    },
  },
};
</script>

<style scoped lang="scss">
.activeTab {
  background-color: rgba(207, 207, 207, 0.25);
}
.v-tab {
  letter-spacing: 0.0178571429em !important;
}
::v-deep {
  .v-input--selection-controls__input .v-icon {
    font-size: 19px;
  }
  .v-slide-group {
    z-index: 99;
    position: sticky;
    top: 0;
    box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12) !important;
  }
}
</style>
